import { ITaxonomyTermSuggestionStats, TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import React from 'react';
import { foldRivalName, RivalTermMatch } from './untrackedRivalDisclosure.utils';

/**
 * Matching cited rival names against the customer's taxonomy terms.
 *
 * Deliberately NOT the cached facet fetch every filter dropdown uses: that one excludes archived
 * terms at the source, and archived is the status this disclosure exists to surface. A rival whose
 * term was archived is precisely the one worth telling an operator about, because the answer is
 * still naming it while the share of voice has stopped counting it.
 */

/**
 * The facets a cited rival could match.
 *
 * Untracked co-subjects are competing offerings and brands, so the search is scoped to the entity
 * facets rather than every taxonomy — an attribute or a locale is never what an answer named as a
 * rival, and listing every facet at every status would fetch the largest population in the project
 * to answer a question about a handful of names.
 */
const RIVAL_FACETS: TaxonomyFacet[] = [
  TaxonomyFacet.Competitor,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.CompetitorProgram,
  TaxonomyFacet.Brand,
  TaxonomyFacet.Product,
];

/** Every status, because archived is the one that matters most here. */
const MATCHABLE_STATUSES: TaxonomyTermStatus[] = [
  TaxonomyTermStatus.Active,
  TaxonomyTermStatus.Unapproved,
  TaxonomyTermStatus.Draft,
  TaxonomyTermStatus.Archived,
];

/**
 * The fields this lookup reads from a term — every one of them declared upstream on the shared
 * taxonomy-term shape.
 *
 * Narrower than `ClientTaxonomyTermEntity` on purpose: the real client entity satisfies this
 * structurally, so the production call site type-checks against it with no cast, while a test can
 * build one of these directly instead of constructing an entity class it does not exercise.
 */
export interface RivalTermLike {
  /**
   * Widened from the client's branded `InternalId` deliberately.
   *
   * The brand is carried by a non-exported unique symbol, so nothing outside the kit can construct
   * one — a test would need a cast to supply an id. This lookup only ever reads the value as a
   * string, and `InternalId` is a subtype of `string`, so declaring what is actually read keeps the
   * real entity assignable here while letting a test pass a plain id. No cast on either side.
   */
  id: string;
  /** Optional for the same reason the URL keeps an id fallback: not every term carries a key yet. */
  key?: string;
  name: string;
  aliases?: string[];
  status: TaxonomyTermStatus;
  suggestionStats?: ITaxonomyTermSuggestionStats;
}

/**
 * The slice of the API this lookup uses.
 *
 * Declared rather than taking the whole `ISyncCoreApi` so the dependency is visible and a test
 * double is a typed object rather than a cast. `ISyncCoreApi` satisfies it structurally.
 */
export interface RivalTermSource {
  service: {
    taxonomyTerms: {
      list: (
        pagination: { page: number; itemsPerPage: number },
        filters: { taxonomyFacet: TaxonomyFacet; statuses: TaxonomyTermStatus[] }
      ) => Promise<{ items?: RivalTermLike[] }>;
    };
  };
}

const EMPTY: ReadonlyMap<string, RivalTermMatch> = new Map();

/**
 * A lookup from folded rival name to the term it matches.
 *
 * Keyed by name AND by every alias, so an operator who aliased a rival's trading name onto its
 * legal one has already answered this question and the disclosure honours it.
 *
 * Loads once per set of names and never throws into the render path: without matches the chip still
 * lists the rivals as plain text, which is the honest fallback.
 */
export function useRivalTermMatches(api: RivalTermSource | undefined, enabled: boolean): ReadonlyMap<string, RivalTermMatch> {
  const [matches, setMatches] = React.useState<ReadonlyMap<string, RivalTermMatch>>(EMPTY);

  React.useEffect(() => {
    if (!api || !enabled) {
      setMatches(EMPTY);
      return;
    }

    let active = true;
    void (async () => {
      try {
        const pages = await Promise.all(
          RIVAL_FACETS.map((facet) =>
            api.service.taxonomyTerms
              .list({ page: 0, itemsPerPage: 500 }, { taxonomyFacet: facet, statuses: MATCHABLE_STATUSES })
              .then((result) => result.items ?? [])
              .catch((): RivalTermLike[] => [])
          )
        );

        if (!active) {
          return;
        }

        const next = new Map<string, RivalTermMatch>();
        for (const [index, items] of pages.entries()) {
          for (const term of items) {
            const match: RivalTermMatch = {
              termId: term.id ? String(term.id) : undefined,
              termKey: term.key ? String(term.key) : undefined,
              facet: RIVAL_FACETS[index],
              status: term.status,
              // Absent rather than zero when the term carries no stats: "never counted" and
              // "counted zero times" are different claims.
              ...(term.suggestionStats ? { seenInRuns: term.suggestionStats.runCount } : {}),
            };

            for (const candidate of [term.name, ...(term.aliases ?? [])]) {
              const folded = foldRivalName(candidate);
              // First match wins, so a name never silently rebinds to a later term.
              if (folded && !next.has(folded)) {
                next.set(folded, match);
              }
            }
          }
        }

        setMatches(next);
      } catch {
        if (active) {
          setMatches(EMPTY);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, enabled]);

  return matches;
}
